
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { useState } from "react";
import { supabase } from "@/lib/supabase";

// Define the form schema with validation
const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters."),
  email: z.string().email("Please enter a valid email address."),
  phone: z.string().optional(),
  trip_type: z.string().optional(),
  destination: z.string().min(2, "Destination is required."),
  travel_dates: z.string().optional(),
  budget: z.string().optional(),
  message: z.string().min(10, "Please provide more details about your request."),
});

export type ItineraryRequestFormValues = z.infer<typeof formSchema>;

export function useItineraryRequestForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize form with validation
  const form = useForm<ItineraryRequestFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      trip_type: "",
      destination: "",
      travel_dates: "",
      budget: "",
      message: "",
    },
  });

  // Handle form submission
  const onSubmit = async (values: ItineraryRequestFormValues) => {
    try {
      setIsSubmitting(true);
      
      // Insert the request into Supabase
      const { error } = await supabase
        .from('itinerary_requests')
        .insert({
          name: values.name,
          email: values.email,
          phone: values.phone || null,
          trip_type: values.trip_type || null,
          destination: values.destination,
          travel_dates: values.travel_dates || null,
          budget: values.budget || null,
          message: values.message,
          status: 'new'
        });
      
      if (error) throw error;
      
      toast.success("Your itinerary request has been submitted. We'll contact you soon!");
      
      // Reset the form
      form.reset();
      
    } catch (error: any) {
      console.error("Error submitting itinerary request:", error);
      toast.error(error.message || "Failed to submit your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
  };
}
